import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  buildCashflowCsvUrl,
  buildCsvUrl,
  cancelPreregistration,
  createPhonePreregistration,
  hardDeletePreregistration,
  extractApiError,
  fetchAdminList,
  fetchCapacity,
  fetchPricing,
  fetchVolunteerMe,
  probeRole,
  restorePreregistration,
  sendBulkReminders,
  sendOneReminder,
  updatePayment,
  updatePreregistrationCounts,
  updatePricing,
} from '../api.js';
import {
  formatReservationEmailDisplay,
  isTechnicalReservationEmail,
} from '../reservationEmailDisplay.js';
import {
  clearVolunteerSession,
  getAdminAuthHeaders,
  getVolunteerSession,
  notifyRolesChanged,
  updateVolunteerMetaFromMe,
} from '../useActiveRoles.js';
import AdminVolunteersPanel from '../components/AdminVolunteersPanel.jsx';
import StaffEmailLogin from '../components/StaffEmailLogin.jsx';

const TOKEN_KEY = 'sqseverac.admin.token';
// Clés localStorage des autres rôles : si on détecte un jeton mal placé sur
// /admin, on le range dans la bonne clé avant de rediriger l'utilisateur.
const SCAN_TOKEN_KEY = 'sqseverac.volunteerToken';
const SAISIE_TOKEN_KEY = 'sqseverac.saisieToken';

function hasAdminAuth(headers) {
  return Boolean(headers && Object.keys(headers).length);
}

// Liste des méthodes de paiement (clé back ⇄ libellé FR ⇄ couleur de chip).
const METHODS = [
  { value: 'cash',     label: 'Espèces',  color: 'success'   },
  { value: 'check',    label: 'Chèque',   color: 'info'      },
  { value: 'card',     label: 'Carte',    color: 'primary'   },
  { value: 'transfer', label: 'Virement', color: 'secondary' },
  { value: 'other',    label: 'Autre',    color: 'default'   },
];
const METHOD_BY_VALUE = Object.fromEntries(METHODS.map((m) => [m.value, m]));

const SORTABLE_COLUMNS = [
  { field: 'created_at',              label: 'Date' },
  { field: 'reservation_rank',        label: 'Type' },
  { field: 'attendance_confirmed_at', label: 'Présence' },
  { field: 'last_name',               label: 'Nom' },
  { field: 'first_name',              label: 'Prénom' },
  { field: 'email',                   label: 'Email' },
  { field: 'phone',                   label: 'Téléphone' },
  { field: 'adults',                  label: 'Ad.',  align: 'right' },
  { field: 'children',                label: 'Enf.', align: 'right' },
  { field: 'meals',                   label: 'Repas', align: 'right' },
  { field: 'due_cents',               label: 'Dû',    align: 'right' },
  { field: 'paid_cents',              label: 'Payé',  align: 'right' },
  { field: 'payment_method',          label: 'Méthode' },
  { field: 'balance_cents',           label: 'Solde', align: 'right' },
  { field: 'last_reminder_at',        label: 'Rappel' },
  { field: 'cancelled_at',            label: 'Statut' },
  { field: 'comment',                 label: 'Commentaire' },
];

const REMINDER_TARGETS = [
  { value: 'unpaid',     label: 'aux personnes pas encore payées' },
  { value: 'partial',    label: 'aux acomptes en cours (solde restant)' },
  { value: 'unsettled',  label: 'à toutes les non-soldées (pas payées + acomptes)' },
  { value: 'all_active', label: 'à toutes les préréservations actives' },
];

const INITIAL_PHONE_DRAFT = {
  last_name: '',
  first_name: '',
  email: '',
  phone: '',
  adults: 1,
  children: 0,
  comment: '',
  send_confirmation_email: true,
};

// Colonne « Actions » figée à droite — reste visible lors du défilement horizontal.
// Definie en fonction du theme pour qu'en dark mode le fond reste opaque et
// coherent avec le reste de la table (sans fond blanc qui rendrait le texte
// blanc invisible). MUI accepte une fonction dans `sx`.
const stickyActionsCell = (theme) => ({
  position: 'sticky',
  right: 0,
  backgroundColor: theme.palette.background.paper,
  borderLeft: 1,
  borderColor: 'divider',
  boxShadow:
    theme.palette.mode === 'light'
      ? '-4px 0 6px -4px rgba(15, 35, 75, 0.12)'
      : '-4px 0 6px -4px rgba(0, 0, 0, 0.45)',
  zIndex: 1,
});
const stickyActionsHeader = (theme) => ({
  ...stickyActionsCell(theme),
  // Header : teinte legerement differente pour distinguer de la zone body.
  backgroundColor:
    theme.palette.mode === 'light' ? '#f5f7fb' : theme.palette.action.hover,
  zIndex: 3, // au-dessus des cellules sticky du body + du stickyHeader
});

const truncatedCell = {
  maxWidth: 240,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatDateShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function formatEur(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function normalize(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Statut de paiement dérivé du couple (paid_cents, due_cents) pour les lignes actives. */
function paymentStatus(row) {
  if (row.cancelled_at) return 'cancelled';
  if (!row.paid_cents) return 'unpaid';
  if (row.paid_cents < row.due_cents) return 'partial';
  return 'paid';
}

/** État de réservation : préréservation (file d'attente) | réservation (place ferme) | annulée. */
function reservationState(row) {
  if (row.cancelled_at) return 'cancelled';
  if (row.confirmed_at) return 'reservation';
  return 'preregistration';
}

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => getAdminAuthHeaders());
  const [draftToken, setDraftToken] = useState('');
  const [list, setList] = useState([]);
  const [capacity, setCapacity] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');

  // Dialog "modifier compteurs" (adultes/enfants).
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState({ adults: 1, children: 0 });
  const [editError, setEditError] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Dialog "encaisser" (montant / méthode / notes).
  const [payEditing, setPayEditing] = useState(null);
  const [payDraft, setPayDraft] = useState({ paidEur: '', method: 'cash', notes: '' });
  const [payError, setPayError] = useState(null);
  const [paySaving, setPaySaving] = useState(false);

  // Dialog "tarifs" (admin → DB).
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [pricingDraft, setPricingDraft] = useState({
    adultEur: '',
    childEur: '',
    musselsGrams: '',
    sausagesGrams: '',
    friesGrams: '',
    mealCapacity: '500',
  });
  const [pricingError, setPricingError] = useState(null);
  const [pricingSaving, setPricingSaving] = useState(false);

  // Menu déroulant "Export".
  const [exportAnchor, setExportAnchor] = useState(null);

  // Menu déroulant "Rappels" + état d'envoi (bulk ou unitaire) + dernier rapport.
  const [reminderAnchor, setReminderAnchor] = useState(null);
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderReport, setReminderReport] = useState(null);
  const [sendingOneId, setSendingOneId] = useState(null);

  /** Dialog « saisie téléphone » (admin crée une préréservation sans passer par le site). */
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState(() => ({ ...INITIAL_PHONE_DRAFT }));
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneError, setPhoneError] = useState(null);

  /** 0 = réservations (défaut), 1 = bénévoles */
  const [adminTab, setAdminTab] = useState(0);

  const isAuth = hasAdminAuth(token);

  const refresh = useCallback(async () => {
    if (!hasAdminAuth(token)) return;
    setLoading(true);
    setError(null);
    try {
      const [rows, cap, pr] = await Promise.all([
        fetchAdminList(token),
        fetchCapacity(),
        fetchPricing(token),
      ]);
      setList(rows);
      setCapacity(cap);
      setPricing(pr);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) {
        setError('Jeton admin invalide.');
        localStorage.removeItem(TOKEN_KEY);
        setToken(getAdminAuthHeaders());
      } else {
        setError(extractApiError(err, 'Erreur lors du chargement.'));
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Session magique déjà présente : rafraîchir les droits (staff_access) puis ouvrir.
  useEffect(() => {
    const session = getVolunteerSession();
    if (!session || isAuth) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchVolunteerMe(session);
        if (cancelled) return;
        updateVolunteerMetaFromMe(me);
        setToken(getAdminAuthHeaders());
      } catch {
        /* session expirée : rester sur l’écran email */
      }
    })();
    return () => { cancelled = true; };
  }, [isAuth]);

  // Login intelligent : on demande au backend à quel rôle correspond le jeton.
  //   * admin  → on continue sur /admin normalement
  //   * scan   → jeton encore reconnu (apéro dormant) ; UI scan retirée → /saisie
  //   * saisie → caisse / accueil bracelets → /saisie
  //   * 401    → message d'erreur, on ne stocke rien
  async function handleLogin(e) {
    e.preventDefault();
    const t = draftToken.trim();
    if (!t) return;
    setError('');
    try {
      const role = await probeRole(t);
      if (role === 'scan') {
        localStorage.setItem(SCAN_TOKEN_KEY, t);
        notifyRolesChanged();
        navigate('/saisie');
        return;
      }
      if (role === 'saisie') {
        localStorage.setItem(SAISIE_TOKEN_KEY, t);
        notifyRolesChanged();
        navigate('/saisie');
        return;
      }
      localStorage.setItem(TOKEN_KEY, t);
      notifyRolesChanged();
      setToken(getAdminAuthHeaders());
    } catch (err) {
      if (err?.response?.status === 401) {
        setError('Jeton inconnu (admin / saisie attendu).');
      } else {
        setError(extractApiError(err, 'Connexion impossible.'));
      }
    }
  }

  function handleLogout() {
    localStorage.removeItem(TOKEN_KEY);
    // Déconnexion complète : la session magique pilotait aussi admin/caisse.
    if (getVolunteerSession()) clearVolunteerSession();
    else notifyRolesChanged();
    setToken(getAdminAuthHeaders());
    setList([]);
    setCapacity(null);
    setPricing(null);
  }

  function openPhoneDialog() {
    setPhoneDraft({ ...INITIAL_PHONE_DRAFT });
    setPhoneError(null);
    setPhoneOpen(true);
  }

  function closePhoneDialog() {
    if (phoneSaving) return;
    setPhoneOpen(false);
  }

  async function handlePhoneSave() {
    const adults = Number(phoneDraft.adults);
    const children = Number(phoneDraft.children);
    if (!Number.isFinite(adults) || adults < 1 || adults > 75) {
      setPhoneError('Nombre d’adultes invalide (1 à 75).');
      return;
    }
    if (!Number.isFinite(children) || children < 0 || children > 75) {
      setPhoneError('Nombre d’enfants invalide (0 à 75).');
      return;
    }
    const em = phoneDraft.email.trim();
    setPhoneSaving(true);
    setPhoneError(null);
    try {
      await createPhonePreregistration(token, {
        last_name: phoneDraft.last_name.trim(),
        first_name: phoneDraft.first_name.trim(),
        phone: phoneDraft.phone.trim(),
        adults,
        children,
        email: em || null,
        comment: phoneDraft.comment.trim() || null,
        send_confirmation_email: em ? phoneDraft.send_confirmation_email : null,
      });
      setPhoneOpen(false);
      await refresh();
    } catch (err) {
      setPhoneError(extractApiError(err, 'Enregistrement impossible.'));
    } finally {
      setPhoneSaving(false);
    }
  }

  async function handleCancel(row) {
    if (!confirm(`Annuler la préréservation de ${row.first_name} ${row.last_name} (${row.meals} repas) ?`)) return;
    try {
      await cancelPreregistration(token, row.id);
      await refresh();
    } catch (err) {
      setError(extractApiError(err, 'Annulation impossible.'));
    }
  }

  /** Réactive une préréservation annulée. */
  async function handleRestore(row) {
    const name = `${row.first_name} ${row.last_name}`;
    if (!confirm(`Restaurer la préréservation de ${name} ?`)) return;
    try {
      await restorePreregistration(token, row.id);
      await refresh();
    } catch (err) {
      setError(extractApiError(err, 'Restauration impossible.'));
    }
  }

  /**
   * Suppression définitive (réservée aux résas déjà annulées).
   * Double confirmation explicite vu que l'action est irréversible
   * (la ligne et tous ses tickets disparaissent de la base).
   */
  async function handleHardDelete(row) {
    const name = `${row.first_name} ${row.last_name}`;
    if (!confirm(
      `Supprimer DÉFINITIVEMENT la préréservation de ${name} ?\n\n` +
      `Cette action est irréversible. Les tickets associés seront aussi supprimés.`,
    )) return;
    try {
      await hardDeletePreregistration(token, row.id);
      await refresh();
    } catch (err) {
      setError(extractApiError(err, 'Suppression définitive impossible.'));
    }
  }

  async function handleSendOneReminder(row) {
    setSendingOneId(row.id);
    setError(null);
    try {
      await sendOneReminder(token, row.id);
      await refresh();
    } catch (err) {
      setError(extractApiError(err, "Envoi du rappel impossible."));
    } finally {
      setSendingOneId(null);
    }
  }

  async function handleBulkReminders(target, label) {
    setReminderAnchor(null);
    // Estimation locale pour l'aperçu côté admin avant l'envoi réel.
    const matching = activeRows.filter((r) => {
      switch (target) {
        case 'unpaid':     return r.paid_cents === 0;
        case 'partial':    return r.paid_cents > 0 && r.paid_cents < r.due_cents;
        case 'unsettled':  return r.paid_cents < r.due_cents;
        case 'all_active': return true;
        default: return false;
      }
    });
    if (matching.length === 0) {
      alert(`Aucune préréservation ne correspond à « ${label} ».`);
      return;
    }
    if (!confirm(`Envoyer un rappel ${label} ?\n\n${matching.length} email${matching.length > 1 ? 's' : ''} vont être envoyé${matching.length > 1 ? 's' : ''}.`)) {
      return;
    }
    setReminderSending(true);
    setError(null);
    setReminderReport(null);
    try {
      const res = await sendBulkReminders(token, target);
      setReminderReport(res);
      await refresh();
    } catch (err) {
      setError(extractApiError(err, 'Envoi groupé impossible.'));
    } finally {
      setReminderSending(false);
    }
  }

  // --- Édition compteurs --------------------------------------------------

  function openEdit(row) {
    setEditing(row);
    setEditDraft({ adults: row.adults, children: row.children });
    setEditError(null);
  }

  function closeEdit() {
    if (editSaving) return;
    setEditing(null);
    setEditError(null);
  }

  async function handleEditSave() {
    if (!editing) return;
    const adults = Number(editDraft.adults);
    const children = Number(editDraft.children);
    if (!Number.isFinite(adults) || adults < 1 || adults > 75) {
      setEditError("Le nombre d'adultes doit être entre 1 et 75.");
      return;
    }
    if (!Number.isFinite(children) || children < 0 || children > 75) {
      setEditError("Le nombre d'enfants doit être entre 0 et 75.");
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updatePreregistrationCounts(token, editing.id, { adults, children });
      setEditing(null);
      await refresh();
    } catch (err) {
      setEditError(extractApiError(err, 'Modification impossible.'));
    } finally {
      setEditSaving(false);
    }
  }

  // --- Encaissement -------------------------------------------------------

  function openPay(row) {
    setPayEditing(row);
    setPayDraft({
      paidEur: row.paid_cents ? (row.paid_cents / 100).toFixed(2) : '',
      method: row.payment_method || 'cash',
      notes: row.payment_notes || '',
    });
    setPayError(null);
  }

  function closePay() {
    if (paySaving) return;
    setPayEditing(null);
    setPayError(null);
  }

  function setPayFull() {
    if (!payEditing) return;
    setPayDraft((d) => ({ ...d, paidEur: (payEditing.due_cents / 100).toFixed(2) }));
  }

  function setPayZero() {
    setPayDraft((d) => ({ ...d, paidEur: '0.00' }));
  }

  async function handlePaySave() {
    if (!payEditing) return;
    const paidEur = Number(payDraft.paidEur);
    if (!Number.isFinite(paidEur) || paidEur < 0 || paidEur > 10000) {
      setPayError('Montant invalide (entre 0 et 10000 €).');
      return;
    }
    const paidCents = Math.round(paidEur * 100);
    if (paidCents > 0 && !payDraft.method) {
      setPayError('Sélectionnez une méthode de paiement.');
      return;
    }
    setPaySaving(true);
    setPayError(null);
    try {
      await updatePayment(token, payEditing.id, {
        paidEur,
        paymentMethod: paidCents > 0 ? payDraft.method : null,
        paymentNotes: payDraft.notes,
      });
      setPayEditing(null);
      await refresh();
    } catch (err) {
      setPayError(extractApiError(err, 'Encaissement impossible.'));
    } finally {
      setPaySaving(false);
    }
  }

  // --- Tarifs -------------------------------------------------------------

  function openPricing() {
    if (!pricing) return;
    setPricingDraft({
      adultEur: (pricing.adult_cents / 100).toFixed(2),
      childEur: (pricing.child_cents / 100).toFixed(2),
      musselsGrams: String(pricing.mussels_portion_grams ?? 330),
      sausagesGrams: String(pricing.sausages_portion_grams ?? 100),
      friesGrams: String(pricing.fries_portion_grams ?? 200),
      mealCapacity: String(pricing.meal_capacity ?? capacity?.capacity ?? 500),
    });
    setPricingError(null);
    setPricingDialogOpen(true);
  }

  function closePricing() {
    if (pricingSaving) return;
    setPricingDialogOpen(false);
    setPricingError(null);
  }

  async function handlePricingSave() {
    const adultEur = Number(pricingDraft.adultEur);
    const childEur = Number(pricingDraft.childEur);
    const musselsPortionGrams = Number.parseInt(pricingDraft.musselsGrams, 10);
    const sausagesPortionGrams = Number.parseInt(pricingDraft.sausagesGrams, 10);
    const friesPortionGrams = Number.parseInt(pricingDraft.friesGrams, 10);
    const mealCapacity = Number.parseInt(pricingDraft.mealCapacity, 10);
    if (!Number.isFinite(adultEur) || adultEur < 0 || adultEur > 1000) {
      setPricingError('Tarif adulte invalide (0 à 1000 €).');
      return;
    }
    if (!Number.isFinite(childEur) || childEur < 0 || childEur > 1000) {
      setPricingError('Tarif enfant invalide (0 à 1000 €).');
      return;
    }
    if (!Number.isFinite(musselsPortionGrams) || musselsPortionGrams < 1 || musselsPortionGrams > 5000) {
      setPricingError('Poids portion moules : entier entre 1 et 5000 g.');
      return;
    }
    if (!Number.isFinite(sausagesPortionGrams) || sausagesPortionGrams < 1 || sausagesPortionGrams > 5000) {
      setPricingError('Poids portion saucisses : entier entre 1 et 5000 g.');
      return;
    }
    if (!Number.isFinite(friesPortionGrams) || friesPortionGrams < 1 || friesPortionGrams > 5000) {
      setPricingError('Poids portion frites : entier entre 1 et 5000 g.');
      return;
    }
    if (!Number.isFinite(mealCapacity) || mealCapacity < 1 || mealCapacity > 20000) {
      setPricingError('Capacité max : entier entre 1 et 20 000 repas.');
      return;
    }
    setPricingSaving(true);
    setPricingError(null);
    try {
      const updated = await updatePricing(token, {
        adultEur,
        childEur,
        musselsPortionGrams,
        sausagesPortionGrams,
        friesPortionGrams,
        mealCapacity,
      });
      setPricing(updated);
      setPricingDialogOpen(false);
      refresh();
    } catch (err) {
      setPricingError(extractApiError(err, 'Mise à jour des tarifs impossible.'));
    } finally {
      setPricingSaving(false);
    }
  }

  // --- Données enrichies (calculs Dû / Solde) -----------------------------

  // Enrichit chaque préréservation des champs comptables `due_cents`, `balance_cents`
  // + un rang numérique `reservation_rank` pour permettre le tri par type :
  //   0 = réservation confirmée, 1 = préréservation en attente, 2 = annulée.
  const enrichedList = useMemo(() => {
    const rankOf = (r) => (r.cancelled_at ? 2 : r.confirmed_at ? 0 : 1);
    if (!pricing) {
      return list.map((r) => ({
        ...r,
        due_cents: 0,
        balance_cents: -r.paid_cents,
        reservation_rank: rankOf(r),
      }));
    }
    return list.map((r) => {
      const due = pricing.adult_cents * r.adults + pricing.child_cents * r.children;
      return {
        ...r,
        due_cents: due,
        balance_cents: due - r.paid_cents,
        reservation_rank: rankOf(r),
      };
    });
  }, [list, pricing]);

  const activeRows = useMemo(() => enrichedList.filter((r) => !r.cancelled_at), [enrichedList]);
  const cancelledRows = useMemo(() => enrichedList.filter((r) => r.cancelled_at), [enrichedList]);
  const activeMeals = useMemo(() => activeRows.reduce((acc, r) => acc + r.meals, 0), [activeRows]);
  const confirmedRows = useMemo(() => activeRows.filter((r) => r.confirmed_at), [activeRows]);
  const pendingRows   = useMemo(() => activeRows.filter((r) => !r.confirmed_at), [activeRows]);
  const confirmedMeals = useMemo(() => confirmedRows.reduce((acc, r) => acc + r.meals, 0), [confirmedRows]);
  const pendingMeals   = useMemo(() => pendingRows.reduce((acc, r) => acc + r.meals, 0), [pendingRows]);
  // Présences confirmées par l'utilisateur (clic dans le mail) : indicateur
  // logistique pour estimer le nombre de gens "engagés" (orthogonal au paiement).
  const attendanceRows = useMemo(
    () => activeRows.filter((r) => r.attendance_confirmed_at),
    [activeRows],
  );
  const attendanceMeals = useMemo(
    () => attendanceRows.reduce((acc, r) => acc + r.meals, 0),
    [attendanceRows],
  );

  // Synthèse comptable : agrégats sur les lignes actives uniquement.
  const totals = useMemo(() => {
    const due = activeRows.reduce((acc, r) => acc + r.due_cents, 0);
    const paid = activeRows.reduce((acc, r) => acc + r.paid_cents, 0);
    const fullyPaid = activeRows.filter((r) => r.paid_cents >= r.due_cents && r.due_cents > 0).length;
    const partials = activeRows.filter((r) => r.paid_cents > 0 && r.paid_cents < r.due_cents).length;
    const unpaid = activeRows.filter((r) => r.paid_cents === 0).length;
    return {
      due,
      paid,
      remaining: Math.max(0, due - paid),
      pct: due > 0 ? Math.min(100, Math.round((paid / due) * 100)) : 0,
      fullyPaid,
      partials,
      unpaid,
    };
  }, [activeRows]);

  const filteredList = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return enrichedList;
    return enrichedList.filter((r) => {
      const full = `${normalize(r.last_name)} ${normalize(r.first_name)}`;
      const reversed = `${normalize(r.first_name)} ${normalize(r.last_name)}`;
      return full.includes(q) || reversed.includes(q);
    });
  }, [enrichedList, query]);

  const sortedList = useMemo(() => {
    const arr = [...filteredList];
    const dir = order === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      // null tri en fin (utile pour cancelled_at, payment_method : remplis d'abord)
      if (av == null && bv == null) return 0;
      if (av == null) return 1 * dir;
      if (bv == null) return -1 * dir;
      return String(av).localeCompare(String(bv), 'fr', { sensitivity: 'base' }) * dir;
    });
    return arr;
  }, [filteredList, orderBy, order]);

  function handleSort(field) {
    if (orderBy === field) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrder('asc');
    }
  }

  // --- Login --------------------------------------------------------------

  if (!isAuth) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: 4, border: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <LockOutlinedIcon color="primary" />
          </Stack>
          <StaffEmailLogin
            audience="admin"
            title="Accès administrateur"
            description="Connectez-vous avec l’email de votre compte bénévole. Le navigateur garde la session ; vos droits (admin / caisse) sont ceux attribués par le super-admin."
            legacyLabel="Jeton ADMIN_API_TOKEN"
            legacyError={error}
            onLegacyToken={async (t) => {
              setDraftToken(t);
              // Réutilise la logique probeRole existante
              setError('');
              try {
                const role = await probeRole(t);
                if (role === 'scan' || role === 'saisie') {
                  localStorage.setItem(role === 'scan' ? SCAN_TOKEN_KEY : SAISIE_TOKEN_KEY, t);
                  notifyRolesChanged();
                  navigate('/saisie');
                  return;
                }
                localStorage.setItem(TOKEN_KEY, t);
                notifyRolesChanged();
                setToken(getAdminAuthHeaders());
              } catch (err) {
                if (err?.response?.status === 401) {
                  setError('Jeton inconnu (admin / saisie attendu).');
                } else {
                  setError(extractApiError(err, 'Connexion impossible.'));
                }
              }
            }}
          />
        </Paper>
      </Container>
    );
  }

  // --- Vue authentifiée ---------------------------------------------------

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4">Administration</Typography>
          <Typography variant="body2" color="text.secondary">
            Réservations repas et gestion des bénévoles.
          </Typography>
        </Box>
        <Tooltip title="Se déconnecter">
          <IconButton onClick={handleLogout} aria-label="Se déconnecter">
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Tabs
        value={adminTab}
        onChange={(_, v) => setAdminTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<RestaurantOutlinedIcon />}
          iconPosition="start"
          label="Réservations"
          id="admin-tab-reservations"
          aria-controls="admin-panel-reservations"
        />
        <Tab
          icon={<GroupOutlinedIcon />}
          iconPosition="start"
          label="Bénévoles"
          id="admin-tab-volunteers"
          aria-controls="admin-panel-volunteers"
        />
      </Tabs>

      {adminTab === 1 && (
        <Box id="admin-panel-volunteers" role="tabpanel" aria-labelledby="admin-tab-volunteers" sx={{ mb: 3 }}>
          <AdminVolunteersPanel embedded />
        </Box>
      )}

      {adminTab === 0 && (
      <Box id="admin-panel-reservations" role="tabpanel" aria-labelledby="admin-tab-reservations">
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5">Réservations</Typography>
          <Typography variant="body2" color="text.secondary">
            Plus récentes d&apos;abord. Cliquez sur les en-têtes pour trier.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Tooltip title="Rafraîchir">
            <span>
              <IconButton onClick={refresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            startIcon={<PhoneInTalkOutlinedIcon />}
            variant="contained"
            color="secondary"
            onClick={openPhoneDialog}
          >
            Saisie téléphone
          </Button>
          <Button
            startIcon={<SettingsOutlinedIcon />}
            variant="outlined"
            onClick={openPricing}
            disabled={!pricing}
          >
            Réglages
          </Button>
          <Button
            startIcon={<CampaignOutlinedIcon />}
            variant="outlined"
            onClick={(e) => setReminderAnchor(e.currentTarget)}
            disabled={reminderSending || list.length === 0}
          >
            {reminderSending ? 'Envoi…' : 'Rappels'}
          </Button>
          <Menu
            anchorEl={reminderAnchor}
            open={Boolean(reminderAnchor)}
            onClose={() => setReminderAnchor(null)}
          >
            {REMINDER_TARGETS.map((t) => (
              <MenuItem key={t.value} onClick={() => handleBulkReminders(t.value, t.label)}>
                <ListItemIcon><EmailOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={`Envoyer ${t.label}`} />
              </MenuItem>
            ))}
          </Menu>
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            onClick={(e) => setExportAnchor(e.currentTarget)}
          >
            Exports
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => { setExportAnchor(null); downloadCsv(token, buildCsvUrl(), 'preregistrations.csv'); }}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Toutes les préréservations"
                secondary="CSV avec colonnes dû / payé / méthode"
              />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setExportAnchor(null); downloadCsv(token, buildCashflowCsvUrl('all'), 'caisse.csv'); }}>
              <ListItemIcon><PaidOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Caisse — toutes méthodes" secondary="Encaissements > 0 €, ligne TOTAL en bas" />
            </MenuItem>
            {METHODS.map((m) => (
              <MenuItem
                key={m.value}
                onClick={() => {
                  setExportAnchor(null);
                  downloadCsv(token, buildCashflowCsvUrl(m.value), `caisse_${m.value}.csv`);
                }}
              >
                <ListItemIcon>
                  <Chip size="small" label=" " color={m.color} sx={{ width: 16, minWidth: 16, '& .MuiChip-label': { px: 0 } }} />
                </ListItemIcon>
                <ListItemText primary={`Caisse — ${m.label.toLowerCase()}`} />
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Stack>

      {capacity && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Chip label={`Capacité : ${capacity.capacity}`} />
            <Chip
              label={`Réservations payées : ${confirmedMeals} repas (${confirmedRows.length})`}
              color="primary"
            />
            <Chip
              label={`Préréservations : ${pendingMeals} repas (${pendingRows.length})`}
              variant="outlined"
              color="primary"
            />
            <Chip
              icon={<HowToRegOutlinedIcon />}
              label={`Présences confirmées : ${attendanceMeals} repas (${attendanceRows.length})`}
              color="success"
              variant="outlined"
            />
            <Chip label={`Total actif : ${activeMeals} repas`} variant="outlined" />
            <Chip label={`Annulées : ${cancelledRows.length}`} variant="outlined" />
            <Chip label={`Restant : ${capacity.remaining}`} color={capacity.is_full ? 'warning' : 'success'} />
          </Stack>
        </Paper>
      )}

      {pricing && activeRows.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
              <PaidOutlinedIcon color="action" />
              <Typography variant="overline" sx={{ letterSpacing: 0.8 }}>Caisse</Typography>
              <Chip label={`Total dû : ${formatEur(totals.due)}`} />
              <Chip label={`Encaissé : ${formatEur(totals.paid)}`} color="success" />
              <Chip
                label={`Reste : ${formatEur(totals.remaining)}`}
                color={totals.remaining === 0 ? 'success' : 'warning'}
                variant={totals.remaining === 0 ? 'filled' : 'outlined'}
              />
              <Chip label={`${totals.fullyPaid} soldées`} size="small" variant="outlined" color="success" />
              <Chip label={`${totals.partials} acomptes`} size="small" variant="outlined" color="info" />
              <Chip label={`${totals.unpaid} non payées`} size="small" variant="outlined" />
            </Stack>
            <Box sx={{ minWidth: { md: 240 }, width: { xs: '100%', md: 240 } }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Avancement caisse</Typography>
                <Typography variant="caption" color="text.secondary"><strong>{totals.pct}%</strong></Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={totals.pct}
                color={totals.pct === 100 ? 'success' : 'primary'}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          </Stack>
        </Paper>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          placeholder="Rechercher par nom ou prénom…"
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 360 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setQuery('')} aria-label="Effacer la recherche">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {query && (
          <Typography variant="body2" color="text.secondary">
            {filteredList.length} résultat{filteredList.length > 1 ? 's' : ''} sur {list.length}
          </Typography>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {reminderReport && (
        <Alert
          severity={reminderReport.failed === 0 ? 'success' : 'warning'}
          onClose={() => setReminderReport(null)}
          sx={{ mb: 2 }}
        >
          <strong>{reminderReport.sent}</strong> rappel{reminderReport.sent > 1 ? 's' : ''} envoyé{reminderReport.sent > 1 ? 's' : ''}
          {reminderReport.failed > 0 && (
            <>
              {' '}— <strong>{reminderReport.failed}</strong> en échec :
              <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                {reminderReport.errors.slice(0, 5).map((e, i) => (
                  <li key={i}><Typography variant="body2">{e}</Typography></li>
                ))}
                {reminderReport.errors.length > 5 && (
                  <li><Typography variant="body2">… et {reminderReport.errors.length - 5} de plus (voir logs)</Typography></li>
                )}
              </Box>
            </>
          )}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider', maxWidth: '100%' }}>
        <Table size="small" stickyHeader sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              {SORTABLE_COLUMNS.map((col) => (
                <TableCell
                  key={col.field}
                  align={col.align || 'left'}
                  sortDirection={orderBy === col.field ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === col.field}
                    direction={orderBy === col.field ? order : 'asc'}
                    onClick={() => handleSort(col.field)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right" sx={stickyActionsHeader}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedList.map((r) => {
              const status = paymentStatus(r);
              const state = reservationState(r);
              const method = r.payment_method ? METHOD_BY_VALUE[r.payment_method] : null;
              return (
                <TableRow key={r.id} hover sx={{ opacity: r.cancelled_at ? 0.5 : 1 }}>
                  <TableCell>{formatDateTime(r.created_at)}</TableCell>
                  <TableCell>
                    {state === 'reservation' && status === 'paid' && (
                      <Tooltip title={r.confirmed_at ? `Soldée le ${formatDateTime(r.paid_at || r.confirmed_at)}` : ''}>
                        <Chip size="small" label="soldée" color="success" />
                      </Tooltip>
                    )}
                    {state === 'reservation' && status !== 'paid' && (
                      <Tooltip title={r.confirmed_at ? `Acompte enregistré le ${formatDateTime(r.confirmed_at)}` : ''}>
                        <Chip size="small" label="réservation" color="primary" />
                      </Tooltip>
                    )}
                    {state === 'preregistration' && (
                      <Chip size="small" label="préréservation" variant="outlined" />
                    )}
                    {state === 'cancelled' && (
                      <Chip size="small" label="—" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {r.attendance_confirmed_at ? (
                      <Tooltip
                        title={`Confirmée par l'utilisateur le ${formatDateTime(r.attendance_confirmed_at)}`}
                      >
                        <Chip
                          size="small"
                          icon={<HowToRegOutlinedIcon fontSize="small" />}
                          label={formatDateShort(r.attendance_confirmed_at)}
                          color="success"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Typography component="span" variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>{r.last_name}</TableCell>
                  <TableCell>{r.first_name}</TableCell>
                  <TableCell sx={truncatedCell}>
                    {isTechnicalReservationEmail(r.email) ? (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {formatReservationEmailDisplay(r.email)}
                      </Typography>
                    ) : (
                      <Tooltip title={r.email}>
                        <span>{r.email}</span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{r.phone}</TableCell>
                  <TableCell align="right">{r.adults}</TableCell>
                  <TableCell align="right">{r.children}</TableCell>
                  <TableCell align="right"><strong>{r.meals}</strong></TableCell>
                  <TableCell align="right">{formatEur(r.due_cents)}</TableCell>
                  <TableCell align="right">
                    {r.paid_cents > 0 ? (
                      <strong>{formatEur(r.paid_cents)}</strong>
                    ) : (
                      <Typography component="span" variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {method ? (
                      <Chip size="small" label={method.label} color={method.color} variant="outlined" />
                    ) : (
                      <Typography component="span" variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {status === 'paid' && <Chip size="small" label="soldé" color="success" />}
                    {status === 'partial' && (
                      <Tooltip title={`Reste ${formatEur(r.balance_cents)}`}>
                        <Chip size="small" label={`+${formatEur(r.balance_cents)}`} color="warning" variant="outlined" />
                      </Tooltip>
                    )}
                    {status === 'unpaid' && !r.cancelled_at && (
                      <Chip size="small" label={formatEur(r.balance_cents)} variant="outlined" />
                    )}
                    {status === 'cancelled' && (
                      <Typography component="span" variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.last_reminder_at ? (
                      <Tooltip title={`Envoyé le ${formatDateTime(r.last_reminder_at)}`}>
                        <Chip
                          size="small"
                          label={formatDateShort(r.last_reminder_at)}
                          variant="outlined"
                          icon={<EmailOutlinedIcon fontSize="small" />}
                        />
                      </Tooltip>
                    ) : (
                      <Typography component="span" variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.cancelled_at ? (
                      <Chip size="small" label="annulée" color="default" />
                    ) : (
                      <Chip size="small" label="active" color="success" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell sx={truncatedCell}>
                    <Tooltip title={r.comment || ''}>
                      <span>{r.comment || '—'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={stickyActionsCell}>
                    {/* Résa annulée : restaurer ou supprimer définitivement (admin only). */}
                    {r.cancelled_at && (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Restaurer cette préréservation">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleRestore(r)}
                          >
                            <RestoreFromTrashOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer définitivement (irréversible)">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleHardDelete(r)}
                          >
                            <DeleteForeverOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                    {!r.cancelled_at && (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={r.paid_cents > 0 ? 'Modifier l\u2019encaissement' : 'Encaisser'}>
                          <IconButton
                            size="small"
                            color={status === 'paid' ? 'success' : 'primary'}
                            onClick={() => openPay(r)}
                          >
                            <PaidOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={r.last_reminder_at ? `Renvoyer un rappel (dernier : ${formatDateTime(r.last_reminder_at)})` : 'Envoyer un rappel par email'}>
                          <span>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleSendOneReminder(r)}
                              disabled={sendingOneId === r.id}
                            >
                              <EmailOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Modifier les compteurs">
                          <IconButton size="small" color="primary" onClick={() => openEdit(r)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Annuler cette préréservation">
                          <IconButton size="small" color="error" onClick={() => handleCancel(r)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {sortedList.length === 0 && (
              <TableRow>
                <TableCell colSpan={SORTABLE_COLUMNS.length + 1} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {query
                    ? `Aucun résultat pour « ${query} ».`
                    : 'Aucune préréservation pour le moment.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>
      )}

      {/* Dialog : saisie téléphone (préréservation créée côté admin) */}
      <Dialog open={phoneOpen} onClose={closePhoneDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvelle préréservation (téléphone)</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Enregistrez les personnes qui appellent sans passer par le site. Si la personne n’a pas d’e-mail, laissez le champ vide : une adresse technique unique sera créée (aucun message automatique).
              Évitez de réutiliser <code>x@x.com</code> pour tout le monde : une seule réservation active est autorisée par adresse e-mail.
            </Typography>
            <TextField
              label="Nom"
              required
              fullWidth
              value={phoneDraft.last_name}
              onChange={(e) => setPhoneDraft((d) => ({ ...d, last_name: e.target.value }))}
              autoFocus
            />
            <TextField
              label="Prénom"
              required
              fullWidth
              value={phoneDraft.first_name}
              onChange={(e) => setPhoneDraft((d) => ({ ...d, first_name: e.target.value }))}
            />
            <TextField
              label="E-mail (facultatif)"
              type="email"
              fullWidth
              value={phoneDraft.email}
              onChange={(e) => {
                const v = e.target.value;
                setPhoneDraft((d) => ({
                  ...d,
                  email: v,
                  send_confirmation_email: v.trim() ? d.send_confirmation_email : false,
                }));
              }}
              helperText="Vide = pas d’e-mail réel (fiche quand même dans la liste + caisse)."
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={phoneDraft.send_confirmation_email}
                  disabled={!phoneDraft.email.trim()}
                  onChange={(e) => setPhoneDraft((d) => ({ ...d, send_confirmation_email: e.target.checked }))}
                />
              )}
              label="Envoyer l’e-mail de confirmation (comme sur le site)"
            />
            <TextField
              label="Téléphone"
              required
              fullWidth
              value={phoneDraft.phone}
              onChange={(e) => setPhoneDraft((d) => ({ ...d, phone: e.target.value }))}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Adultes"
                type="number"
                fullWidth
                value={phoneDraft.adults}
                onChange={(e) => setPhoneDraft((d) => ({ ...d, adults: e.target.value }))}
                inputProps={{ min: 1, max: 75, step: 1 }}
              />
              <TextField
                label="Enfants"
                type="number"
                fullWidth
                value={phoneDraft.children}
                onChange={(e) => setPhoneDraft((d) => ({ ...d, children: e.target.value }))}
                inputProps={{ min: 0, max: 75, step: 1 }}
              />
            </Stack>
            <TextField
              label="Commentaire (facultatif)"
              fullWidth
              multiline
              minRows={2}
              value={phoneDraft.comment}
              onChange={(e) => setPhoneDraft((d) => ({ ...d, comment: e.target.value }))}
              inputProps={{ maxLength: 500 }}
            />
            {phoneError && <Alert severity="error">{phoneError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePhoneDialog} disabled={phoneSaving}>Annuler</Button>
          <Button onClick={handlePhoneSave} variant="contained" disabled={phoneSaving}>
            {phoneSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog : modification compteurs adultes/enfants */}
      <Dialog open={Boolean(editing)} onClose={closeEdit} maxWidth="xs" fullWidth>
        <DialogTitle>
          Modifier les compteurs
          {editing && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {editing.first_name} {editing.last_name} —{' '}
              {formatReservationEmailDisplay(editing.email)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre d'adultes"
              type="number"
              fullWidth
              value={editDraft.adults}
              onChange={(e) => setEditDraft((d) => ({ ...d, adults: e.target.value }))}
              inputProps={{ min: 1, max: 75, step: 1 }}
              autoFocus
            />
            <TextField
              label="Nombre d'enfants"
              type="number"
              fullWidth
              value={editDraft.children}
              onChange={(e) => setEditDraft((d) => ({ ...d, children: e.target.value }))}
              inputProps={{ min: 0, max: 75, step: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Total :{' '}
              <strong>
                {Number(editDraft.adults || 0) + Number(editDraft.children || 0)} repas
              </strong>
              {editing && (
                <>
                  {' '}(avant : {editing.adults + editing.children})
                </>
              )}
            </Typography>
            {editError && <Alert severity="error">{editError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={editSaving}>Annuler</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={editSaving}>
            {editSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog : encaissement (montant / méthode / notes, acompte autorisé) */}
      <Dialog open={Boolean(payEditing)} onClose={closePay} maxWidth="xs" fullWidth>
        <DialogTitle>
          Encaissement
          {payEditing && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {payEditing.first_name} {payEditing.last_name} — {payEditing.meals} repas
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {payEditing && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Paper
                variant="outlined"
                sx={(theme) => ({
                  p: 1.5,
                  bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : 'action.hover',
                })}
              >
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography variant="caption" color="text.secondary">À régler</Typography>
                    <Typography variant="h6">{formatEur(payEditing.due_cents)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Déjà perçu</Typography>
                    <Typography variant="h6">{formatEur(payEditing.paid_cents)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Solde actuel</Typography>
                    <Typography variant="h6" color={payEditing.balance_cents > 0 ? 'warning.main' : 'success.main'}>
                      {formatEur(payEditing.balance_cents)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <TextField
                label="Montant total perçu (€)"
                type="number"
                fullWidth
                value={payDraft.paidEur}
                onChange={(e) => setPayDraft((d) => ({ ...d, paidEur: e.target.value }))}
                inputProps={{ min: 0, max: 10000, step: 0.01 }}
                autoFocus
                helperText="Saisir le total cumulé (acomptes inclus), pas un delta."
              />
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={setPayFull}>
                  Solder ({formatEur(payEditing.due_cents)})
                </Button>
                <Button size="small" variant="outlined" color="inherit" onClick={setPayZero}>
                  Remettre à 0
                </Button>
              </Stack>

              <TextField
                label="Méthode"
                select
                fullWidth
                value={payDraft.method}
                onChange={(e) => setPayDraft((d) => ({ ...d, method: e.target.value }))}
                SelectProps={{ native: true }}
                disabled={Number(payDraft.paidEur) === 0}
              >
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </TextField>

              <TextField
                label="Notes (n° de chèque, banque…)"
                fullWidth
                multiline
                minRows={2}
                value={payDraft.notes}
                onChange={(e) => setPayDraft((d) => ({ ...d, notes: e.target.value }))}
              />

              {payError && <Alert severity="error">{payError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePay} disabled={paySaving}>Annuler</Button>
          <Button onClick={handlePaySave} variant="contained" disabled={paySaving}>
            {paySaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog : édition des tarifs (DB) */}
      <Dialog open={pricingDialogOpen} onClose={closePricing} maxWidth="sm" fullWidth>
        <DialogTitle>
          Réglages réservations
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Capacité max, tarifs, et poids buffet (stats cuisine).
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre maximum de repas"
              type="number"
              fullWidth
              value={pricingDraft.mealCapacity}
              onChange={(e) => setPricingDraft((d) => ({ ...d, mealCapacity: e.target.value }))}
              inputProps={{ min: 1, max: 20000, step: 1 }}
              helperText="Affiché sur l’accueil. À ce nombre, les inscriptions en ligne sont fermées. Elles rouvrent si le total redescend. Le club peut encore enregistrer par téléphone."
              autoFocus
            />
            <Divider />
            <Typography variant="subtitle2">Tarifs</Typography>
            <TextField
              label="Tarif adulte (€)"
              type="number"
              fullWidth
              value={pricingDraft.adultEur}
              onChange={(e) => setPricingDraft((d) => ({ ...d, adultEur: e.target.value }))}
              inputProps={{ min: 0, max: 1000, step: 0.01 }}
            />
            <TextField
              label="Tarif enfant (€)"
              type="number"
              fullWidth
              value={pricingDraft.childEur}
              onChange={(e) => setPricingDraft((d) => ({ ...d, childEur: e.target.value }))}
              inputProps={{ min: 0, max: 1000, step: 0.01 }}
            />
            <Divider />
            <Typography variant="subtitle2">Poids indicatif par portion scannée (g)</Typography>
            <TextField
              label="Moules (g / portion)"
              type="number"
              fullWidth
              value={pricingDraft.musselsGrams}
              onChange={(e) => setPricingDraft((d) => ({ ...d, musselsGrams: e.target.value }))}
              inputProps={{ min: 1, max: 5000, step: 1 }}
            />
            <TextField
              label="Saucisses (g / portion)"
              type="number"
              fullWidth
              value={pricingDraft.sausagesGrams}
              onChange={(e) => setPricingDraft((d) => ({ ...d, sausagesGrams: e.target.value }))}
              inputProps={{ min: 1, max: 5000, step: 1 }}
            />
            <TextField
              label="Frites (g / portion)"
              type="number"
              fullWidth
              value={pricingDraft.friesGrams}
              onChange={(e) => setPricingDraft((d) => ({ ...d, friesGrams: e.target.value }))}
              inputProps={{ min: 1, max: 5000, step: 1 }}
            />
            {pricingError && <Alert severity="error">{pricingError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePricing} disabled={pricingSaving}>Annuler</Button>
          <Button onClick={handlePricingSave} variant="contained" disabled={pricingSaving}>
            {pricingSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

async function downloadCsv(auth, url, filename) {
  try {
    const headers =
      auth && typeof auth === 'object'
        ? auth
        : { 'X-Admin-Token': auth };
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch (err) {
    alert(`Export impossible : ${err.message}`);
  }
}
